import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Phone, Shield, ArrowRight } from "lucide-react";
import api, { authStore } from "../lib/api";

/**
 * Auth Modal — Mobile → SMS OTP → JWT + Routing decision (3 Rules).
 * On successful verification, navigates per backend `route` field:
 *  - RULE A (complete)   → /home
 *  - RULE B (new user)   → /register?step=1
 *  - RULE C (incomplete) → /register?step=<next_step>
 */
export default function AuthModal({ open, onClose }) {
  const nav = useNavigate();
  const [stage, setStage] = useState("mobile");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [devOtp, setDevOtp] = useState(null);

  if (!open) return null;

  const reset = () => { setStage("mobile"); setMobile(""); setOtp(""); setDevOtp(null); };
  const closeAll = () => { reset(); onClose(); };

  const sendOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(mobile)) return toast.error("Enter a valid 10-digit mobile");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/send-otp", { mobile });
      setDevOtp(data.dev_otp || null);
      setStage("otp");
      toast.success("OTP sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    } finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) return toast.error("Enter a valid 6-digit OTP");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { mobile, otp });
      authStore.save(data.token, data.user);
      const route = data.route || {};
      toast.success(
        route.kind === "admin"    ? "Admin access granted"
        : route.kind === "home"   ? "Welcome back"
        : route.kind === "wizard_resume" ? "Resuming your registration"
        : "Let's begin"
      );
      closeAll();
      if (route.kind === "admin") {
        nav("/admin");
      } else if (route.kind === "home") {
        nav("/home");
      } else {
        nav(`/register?step=${route.next_step || 1}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid OTP");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="auth-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(26,20,16,0.72)" }}>
      <div className="relative w-full max-w-md" style={{ background: "var(--ivory)", border: "1px solid var(--gold)" }}>
        <button data-testid="auth-modal-close" onClick={closeAll} aria-label="Close"
          className="absolute right-3 top-3 p-2" style={{ color: "var(--ash)" }}>
          <X size={18}/>
        </button>

        <div className="p-7 md:p-9">
          <div className="eyebrow">Register · Sacred Resolve</div>
          <h3 className="font-serif text-2xl md:text-3xl mt-2" style={{ color: "var(--crimson)" }}>
            Register for Marriage
          </h3>

          {stage === "mobile" && (
            <form onSubmit={sendOtp} className="mt-6 space-y-5">
              <div>
                <label className="input-label">Mobile Number</label>
                <div className="flex items-center gap-2">
                  <Phone size={16} style={{ color: "var(--crimson)" }}/>
                  <input data-testid="auth-mobile" required type="tel" pattern="[0-9]{10}"
                    value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                    placeholder="10-digit mobile" className="underline-input"/>
                </div>
              </div>
              <button data-testid="auth-send-otp" type="submit" disabled={busy}
                className="btn-primary w-full justify-center">
                {busy ? "Sending…" : "Send OTP"} <ArrowRight size={16}/>
              </button>
              <p className="text-[11px] leading-relaxed text-center" style={{ color: "var(--ash)" }}>
                SMS OTP via <strong style={{ color: "var(--ink)" }}>MSG91</strong> — currently using dev/mock delivery.
              </p>
            </form>
          )}

          {stage === "otp" && (
            <form onSubmit={verifyOtp} className="mt-6 space-y-5">
              <div className="p-4" style={{ background: "var(--bone)", border: "1px solid var(--line)" }}>
                <div className="flex items-center gap-2">
                  <Shield size={14} style={{ color: "var(--leaf)" }}/>
                  <span className="text-xs" style={{ color: "var(--ash)" }}>
                    OTP sent to <strong style={{ color: "var(--ink)" }}>+91 {mobile}</strong>
                  </span>
                </div>
                {devOtp && (
                  <p className="mt-2 text-[11px]" style={{ color: "var(--ash)" }}>
                    Dev OTP: <code>{devOtp}</code> (remove in production)
                  </p>
                )}
              </div>
              <div>
                <label className="input-label">Enter 6-digit OTP</label>
                <input data-testid="auth-otp" required type="tel" pattern="[0-9]{6}" maxLength={6}
                  value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="••••••" className="underline-input tracking-[0.5em] text-center"/>
              </div>
              <button data-testid="auth-verify-otp" type="submit" disabled={busy}
                className="btn-primary w-full justify-center">
                {busy ? "Verifying…" : "Verify & Continue"} <ArrowRight size={16}/>
              </button>
              <button type="button" onClick={() => setStage("mobile")}
                className="w-full text-xs uppercase tracking-widest" style={{ color: "var(--ash)" }}>
                ← Change mobile
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
